import "./FAQ.css";
import { useState, useRef } from "react";

function FAQ({ open, onClose }) {

  // ================= DRAG SYSTEM =================
  const [pos, setPos] = useState({ x: 250, y: 80 });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const startDrag = (e) => {
    dragging.current = true;
    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
  };

  const onDrag = (e) => {
    if (!dragging.current) return;

    setPos({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  };

  const stopDrag = () => {
    dragging.current = false;
  };

  // ================= FAQ DATA =================
  const faqData = [
    {
        q: "Can NK Tech help in career development?",
        a: `Yes, NK Tech is designed to enhance technical understanding and practical awareness of industrial process operations.

        It helps users develop confidence in handling DCS systems, interpreting process data, and understanding plant behavior.

        These skills are valuable for roles in oil and gas production, refining, petrochemicals, and process engineering industries.

        By simulating real operational scenarios, it supports continuous learning and professional growth in industrial environments.`
        },
    {
        q: "What makes NK Tech different from other simulators?",
        a: `NK Tech focuses on combining engineering accuracy with real-time interactive simulation, rather than static training models.

        It integrates live-like process behavior, control logic visualization, and production analytics in a single platform.

        The system is designed with an industrial mindset, reflecting real operational challenges such as alarms, fluctuations, and system responses.

        This makes it more aligned with actual plant experience compared to traditional theoretical training tools.`
        },
    {
        q: "What is the purpose of this DCS simulator?",
        a: `
        This simulator is designed as a digital training and operational awareness platform for Distributed Control System (DCS) environments commonly used in oil, gas, and process industries. It provides a realistic interface for understanding plant operations without interacting with physical equipment.

        It replicates key process systems such as crude handling, gas processing, flare systems, and produced water management using live or simulated data tags. This allows users to observe system behavior under different operating conditions in real time.

        The primary objective is to improve operator familiarity, process understanding, alarm response awareness, and system navigation skills. It is especially useful for trainees, engineers, and control room personnel preparing for real plant environments.

        This system does not perform or control real plant operations and should not be interpreted as a certified control system. It is strictly intended for training, simulation, and digital twin visualization purposes only.
        `
        },
    {
        q: "Is this connected to a real plant?",
        a: `
        This system is designed as a digital twin and training simulator for Distributed Control System (DCS) environments. It may operate using either simulated process models or live data tag integrations depending on configuration.

        In cases where live tags are connected, the data is strictly read-only and used only for visualization, monitoring, and training purposes. No control commands or write-back actions are executed on the physical plant systems.

        The simulator is built to replicate real plant behavior such as flow variations, alarm conditions, and process responses to help users understand operational dynamics in a safe environment.

        Regardless of configuration, this platform is not intended for direct plant control. It is strictly used for training, familiarization, and operator competency development within a controlled digital environment.
        `
        },
    {
        q: "Is NK Tech used in real industrial training?",
        a: `Yes, NK Tech solutions are designed for industrial training, simulation-based learning, and process familiarization.

        They are used to support engineers and operators in understanding complex plant operations before working in live environments.

        The system is structured to reflect real-world industrial standards, making it suitable for competency development programs.

        However, it remains a training and digital twin platform and does not replace certified field operations.`
        },
    {
        q: "Where should I start as a new user?",
        a: `
        As a new user, it is recommended to begin with the overall system overview starting from the top bar interface. This helps you understand the main navigation structure of the DCS simulator before interacting with any process elements.

        The next step is to observe the alarm system, as it provides real-time insight into process conditions, equipment status, and abnormal situations. Understanding alarms first helps you build awareness of how the system responds to process changes.

        After familiarizing yourself with alarms, proceed to the Production Summary section. This gives you a high-level view of plant performance, including crude, gas, flare, and produced water trends, which are essential for understanding overall process flow.

        Finally, explore individual equipment faceplates such as pumps, valves, and transmitters. This will help you understand detailed process behavior, control logic interaction, and how local equipment contributes to overall plant operations.
        `
        },
    {
        q: "What is the purpose of this DCS simulator?",
        a: `This simulator is designed for industrial training and process familiarization within a virtual Distributed Control System (DCS) environment. It replicates real plant operations such as monitoring, control logic interaction, alarm handling, and production tracking.

        It provides a safe digital twin environment where users can observe and understand how offshore and onshore production facilities operate without risk to real equipment or personnel.

        The system is intended for learning process behavior, operational decision-making, and understanding control system responses under different conditions.

        It should be used strictly for educational, training, and simulation purposes and not as a substitute for real plant control systems.`
        },
    {
        q: "What does NK Tech DCS Simulator provide?",
        a: `The NK Tech DCS Simulator provides a virtual industrial control environment that replicates real plant operations in real time.

        It includes process monitoring dashboards, alarm systems, production tracking, and equipment-level interactions.

        Users can observe how flow, pressure, and production systems respond dynamically under different operational conditions.

        This helps learners build practical understanding of industrial automation and control system behavior.`
        },
    {
        q: "Why are values changing every second?",
        a: `The continuously changing values are designed to simulate real-time process dynamics within a live Distributed Control System (DCS) environment.

        This behavior reflects how actual industrial process variables fluctuate due to feed variations, control loop responses, equipment performance, and environmental conditions.

        In real plant operations, parameters such as flow, pressure, and temperature are never completely static, and this simulator replicates that behavior for training realism.

        It allows users to understand process stability, transient behavior, and control system response in a dynamic operational environment.`
        },
    {
        q: "What is Production Summary?",
        a: `Production Summary is a high-level operational overview of the plant’s real-time or simulated production performance.

        It aggregates key process streams such as crude oil production, gas export, gas flaring, and produced water handling into a single monitoring interface.

        This allows operators and trainees to quickly understand overall plant efficiency, production balance, and export performance without going into individual equipment details.

        It serves as a decision-support dashboard for observing trends, comparing performance, and identifying process deviations within the simulated DCS environment.`
        },
    {
        q: "What is NK Tech?",
        a: `NK Tech is an industrial technology and engineering solutions platform focused on process simulation, digital twin development, and DCS training systems.

        It is designed to bridge the gap between classroom learning and real plant operations by providing realistic, interactive industrial environments.

        The platform integrates engineering concepts such as process control, instrumentation, and production monitoring into a unified learning experience.

        NK Tech is built to support capacity development for engineers and operators in the oil and gas and process industries.`
        },
    {
        q: "What is a DCS system?",
        a: `A Distributed Control System (DCS) is an industrial automation system used to monitor and control complex processes across a plant or facility.

        It consists of multiple controllers distributed throughout the plant, each responsible for a specific section of the process, while being centrally monitored from operator workstations.

        DCS systems are widely used in oil and gas, refining, petrochemical, and power plants because they provide high reliability, real-time control, and improved operational safety.

        In this simulator, the DCS concept is replicated to help users understand how industrial process control, alarms, interlocks, and operator actions work in a structured control environment.`
        },
    {
        q: "What is a digital twin?",
        a: `A digital twin is a virtual representation of a physical industrial system, such as a plant, process unit, or equipment setup.

        It mirrors real-world operations by replicating process behavior, instrumentation data, and system responses in a digital environment.

        This allows engineers and operators to simulate scenarios, test changes, and study system performance without impacting actual plant operations.

        In this DCS simulator, the digital twin concept is used to support training, process understanding, and operational decision-making in a safe and controlled environment.`
        },
    {
        q: "Can I logout and return later?",
        a: `Yes, you can safely log out and return later without losing your session context.

        In most configurations of this simulator, basic session information such as user identity, login time, and access level is stored locally in the browser (or assigned session storage), allowing continuity when you reopen the system.

        However, active real-time process simulation values may reset depending on how the simulator is configured or whether a fresh simulation cycle is initiated on login.

        This ensures flexibility for training sessions while maintaining a consistent user experience across multiple visits.`
        },
    {
        q: "Why are some values zero?",
        a: `Zero values typically indicate that there is no active flow or that the equipment associated with that tag is currently not operating.

        In some cases, it may also mean that the control valve is fully closed, the pump is not running, or the process unit is in a standby or shutdown condition.

        Another possible reason is that the tag has not yet received updated simulation data or is not actively linked to a live or simulated signal source.

        In all cases, zero readings are used intentionally to reflect realistic plant conditions where processes can be dynamic, intermittent, or temporarily inactive.`
        },
    {
        q: "What is flare?",
        a: `A flare system is an essential safety component in industrial process plants used for the controlled combustion of excess hydrocarbons or gas.

        It is designed to safely dispose of unwanted or emergency gas releases from process units, preventing overpressure conditions and protecting equipment integrity.

        Flare systems are typically activated during startup, shutdown, process upsets, or emergency situations where gas cannot be recovered or processed safely.

        In this simulator, the flare represents a key safety and environmental control mechanism, helping users understand how plants manage pressure relief and operational safety in real-world conditions.`
        },
    {
        q: "Is this system safe for training?",
        a: `Yes, the simulator is designed to be completely safe for training and educational use.

        All process values, equipment interactions, and control responses are simulated within a controlled digital environment that does not affect any real physical plant or operational system.

        This ensures users can freely explore system behavior, make mistakes, and learn from different operational scenarios without risk to personnel, equipment, or production.

        The goal is to provide a realistic but risk-free environment for developing understanding of industrial DCS operations and process control principles.`
        },
    {
        q: "What is BOE?",
        a: `BOE stands for Barrels of Oil Equivalent, a standard unit used in the oil and gas industry to compare and combine different forms of energy production.

        It converts gas, condensate, and other hydrocarbon outputs into an equivalent number of barrels of crude oil based on energy content.

        This makes it easier to evaluate total production performance across multiple energy streams using a single standardized metric.

        In this simulator, BOE is used to help users understand overall production efficiency and compare contributions from different wells and process units in a unified way.`
        },
    {
        q: "What is produced water?",
        a: `Produced water is the water that is naturally present in hydrocarbon reservoirs and is brought to the surface during oil and gas production.

        It is separated from crude oil and gas during processing in separation units and contains traces of hydrocarbons, salts, and other dissolved impurities.

        Before disposal or reuse, it undergoes treatment processes such as oil-water separation, filtration, and sometimes chemical treatment to meet environmental discharge standards.

        In this simulator, produced water helps users understand separation efficiency, environmental management, and the importance of treating waste streams in industrial operations.`
        },
    {
        q: "Who is this for?",
        a: `This simulator is designed for a wide range of users involved in industrial process operations and control systems.

        It is particularly useful for process engineers, control room operators, field operators, and maintenance personnel who want to improve their understanding of plant operations.

        It is also suitable for students, trainees, and early-career professionals in oil and gas, refining, and petrochemical industries who need practical exposure to DCS environments.

        Overall, it serves as a learning and development platform for anyone interested in industrial process control, digital twin systems, and operational safety awareness.`
        },
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  // ================= EARLY RETURN (AFTER HOOKS) =================
  if (!open) return null;

  return (
    <div
      className="faq-overlay"
      onMouseMove={onDrag}
      onMouseLeave={stopDrag}
      onMouseUp={stopDrag}
    >
      <div
        className="faq-window"
        style={{
          left: pos.x,
          top: pos.y,
          position: "absolute",
        }}
      >
        {/* HEADER */}
        <div className="faq-header" onMouseDown={startDrag}>
          <div className="faq-title-center">
            <div className="faq-main-title">
              NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
            </div>
            <div className="faq-sub-title">
              FREQUENTLY ASKED QUESTIONS (FAQ)
            </div>
          </div>

          <button className="faq-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="faq-body">

          {/* LEFT */}
          <div className="faq-left">
            {faqData.map((item, index) => (
              <div
                key={index}
                className={`faq-question ${activeIndex === index ? "active" : ""}`}
                onClick={() => setActiveIndex(index)}
              >
                {item.q}
              </div>
            ))}
          </div>

          {/* RIGHT */}
          <div className="faq-right">
            <div className="faq-answer">
              {faqData[activeIndex].a}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default FAQ;